import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Conversation, Message } from "src/types/conversations";
import { useDispatch } from "react-redux";
import dateformat from "dateformat";
import { fetchMessage } from "src/redux/actions/conversation";
import { RootState } from "src/redux/store";
import CollectionWidgetManager from "src/services/collections.widget.service";
import DOMPurify from "dompurify";
import { useSelector } from "react-redux";

import Slider from "src/components/slider";
import Collapse from "src/pages/asset/components/animation/collapse";
import { PreviewLoader } from "../loaders";

import _ from "lodash";
import styles from "./preview.module.scss";
import cx from "classnames";

interface IPreview {
  conversation: Conversation;
  show: boolean;
  onClose: () => void;
}

const Preview: React.FC<IPreview> = ({ conversation, show, onClose }) => {
  interface InitialMessageType {
    item: Message;
    loaded: boolean;
    loading: boolean;
  }

  //constants
  const dispatch = useDispatch();
  const initialMessageState: InitialMessageType = {
    item: {
      id: "",
      body: "",
      recipients: [],
      subject: "",
    },
    loaded: false,
    loading: false,
  };
  const widgetManager = useRef(new CollectionWidgetManager());

  //selectors
  const { item, loaded, loading, assets, sections } = useSelector(
    (state: RootState) => {
      const conversationState = _.get(
        state.conversations.conversation,
        `${conversation.id}`,
      );
      const message = _.get(conversationState, "message", initialMessageState);

      return {
        item: message.item,
        loaded: message.loaded,
        loading: message.loading,
        assets: conversationState.assets,
        sections: conversationState.sections,
      };
    },
  );

  //states
  const [expandRecipients, setExpandRecipients] = React.useState(false);

  //side effects
  useEffect(() => {
    if (show && !loaded) {
      dispatch(fetchMessage({ shareId: conversation.id! }));
    }

    if (!show) {
      setExpandRecipients(false);
    }
  }, [show]);

  //memorizing the recipients calculation
  const title = useMemo(() => {
    if (_.get(conversation, "collection")) {
      return _.get(conversation, "collection.name", "");
    } else if (_.get(conversation, "subject")) {
      return _.get(conversation, "subject");
    } else {
      return "No Subject";
    }
  }, [conversation.subject]);

  const renderMailBody = useCallback(() => {
    const html = widgetManager.current.getRawTemplate(_.get(item, "body"));
    const node = document.createElement("div");
    node.innerHTML = html;

    let assetsWidgets = node.querySelectorAll(".assetsGrid");

    if (conversation.sections && conversation.sections.length > 0) {
      assetsWidgets = node.querySelectorAll(".sectionCard");
    }

    for (let i = 0; i < assetsWidgets.length; i++) {
      const assetWidget = assetsWidgets[i];

      const imgNodes: HTMLImageElement[] = Array.from(
        assetWidget.querySelectorAll(".mceNonEditable img"),
      ) as HTMLImageElement[];
      updateImage(imgNodes, conversation);
    }

    const anchorTag = node.querySelectorAll(".mceNonEditable a");
    if (anchorTag && anchorTag.length > 0) {
      for (let i = 0; i < anchorTag.length; i++) {
        const element = anchorTag[i] as HTMLAnchorElement;

        element.href = "";
        element.setAttribute(
          "style",
          "pointer-events: none; font-weight: 600; font-size: 0.875em; color: #353535; font-family: 'Gilroy', Helvetica, Arial, sans-serif; text-decoration: none;",
        );
      }
    }

    return (
      <div
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(node.innerHTML) }}
      ></div>
    );
  }, [item]);

  const recipients = useMemo(() => {
    if (item) {
      let to =
        _.get(item, "recipients", [])
          .filter((recipient) => recipient.type === "TO")
          .map((item) => item.contact.email) || [];
      const allToAddresses = to;
      const cc =
        _.get(item, "recipients", [])
          .filter((recipient) => recipient.type === "CC")
          .map((item) => item.contact.email) || [];
      const bcc =
        _.get(item, "recipients", [])
          .filter((recipient) => recipient.type === "BCC")
          .map((item) => item.contact.email) || [];
      if (to?.length > 3) {
        const noOfTotalRecipients = to.length + cc.length + bcc.length;
        to = to.slice(0, 2);
        return {
          to: to.join(", ") + ` + ${noOfTotalRecipients - 2} more`,
          cc: cc.join(", "),
          bcc: bcc.join(", "),
          allToAddresses: allToAddresses.join(", "),
        };
      }
      return {
        to: to.join(", "),
        cc: cc.join(", "),
        bcc: bcc.join(", "),
        allToAddresses: allToAddresses.join(", "),
      };
    } else {
      return {
        to: "",
        cc: "",
        bcc: "",
        allToAddresses: "",
      };
    }
  }, [item]);

  const updateImage = (imgNodes: HTMLImageElement[], share: any) => {
    const DefaultIcon = "/images/defaultIcons/default_asset.jpg"
    if (imgNodes && imgNodes.length > 0) {
      if (assets && assets.length > 0) {
        for (let i = 0; i < imgNodes.length; i++) {
          const element = imgNodes[i];

          const asset = assets[i];

          if (asset) {
            const image = asset.icon ? asset.icon.thumbnail : DefaultIcon;
            element.src = image || "";
          }
        }
      } else if (sections) {
        const element = imgNodes[0];
        const image = share.icon ? share.icon.thumbnail : DefaultIcon;
        element.src = image;
      }
    }
  };

  const shouldShowDropDown = () => {
    if (
      recipients.allToAddresses.split(",").length > 3 ||
      recipients.cc ||
      recipients.bcc
    ) {
      return true;
    } else {
      return false;
    }
  };
  
  //render
  return (
    <Slider.Dialog show={show} onClose={onClose} width="650px">
      <Slider.Header className={styles.header}>
        <div className={styles.titleContainer}>
          <p className={styles.title}>preview mail</p>
          <p className={styles.subtitle}>
            {dateformat(conversation.createdDate, "dd mmmm yyyy, hh:mm tt")}
          </p>
        </div>

        <i className="paperflite-close" onClick={onClose}></i>
      </Slider.Header>
      <Slider.Body className={styles.body}>
        {loading && <PreviewLoader />}
        {loaded && (
          <section className={styles.email}>
            <div className={styles.recipients}>
              <div className={cx(styles.mailContainer, styles.to)}>
                <label className={styles.label}>
                  To <span>:</span>{" "}
                </label>
                <span className={styles.mails}>
                  {!expandRecipients
                    ? recipients.to
                    : recipients.allToAddresses}
                </span>

                {shouldShowDropDown() && (
                  <i
                    className={cx("paperflite-dropdown", {
                      [styles.expandRecipients]: expandRecipients,
                    })}
                    onClick={() => setExpandRecipients(!expandRecipients)}
                  />
                )}
              </div>

              <Collapse isOpen={expandRecipients}>
                {recipients.cc && (
                  <div className={styles.mailContainer}>
                    <label className={styles.label}>
                      CC <span>:</span>{" "}
                    </label>
                    <span className={styles.mails}>{recipients.cc}</span>
                  </div>
                )}

                {recipients.bcc && (
                  <div className={styles.mailContainer}>
                    <label className={styles.label}>
                      BCC <span>:</span>{" "}
                    </label>
                    <span className={styles.mails}>{recipients.bcc}</span>
                  </div>
                )}
              </Collapse>
            </div>
            <div className={styles.content}>
              <div className={styles.subject}>{title}</div>
              {renderMailBody()}
            </div>
          </section>
        )}
      </Slider.Body>
    </Slider.Dialog>
  );
};

export default Preview;
